import type { LucideIcon } from "lucide-react";

/** Lifecycle/management actions only — formatting excluded. All ids kebab-case. */
export type ObjectActionId =
  // Navigation
  | "open"
  | "open-in-new-tab"
  | "locate-on-canvas"
  | "edit"
  | "manage"
  // Organization
  | "rename"
  | "move"
  | "duplicate"
  | "copy"
  | "new-folder"
  | "new-document"
  | "new-canvas"
  | "new-chat"
  | "upload-files"
  | "copy-to-workspace"
  | "copy-url"
  | "wrap-in-section"
  | "remove-from-canvas"
  | "show-on-canvas"
  | "add-to-my-skills"
  // Sharing & access
  | "share"
  | "add-members"
  | "edit-workspace"
  // Favorites
  | "star"
  | "unstar"
  // File-specific
  | "download"
  | "publish"
  | "unpublish"
  | "version-history"
  // Skill mode
  | "set-auto"
  | "set-manual"
  // Canvas ordering
  | "bring-to-front"
  | "send-to-back"
  // Collaboration
  | "chat-about"
  | "execute-action-item"
  // Destructive
  | "delete"
  | "delete-wrapper-only"
  | "restore"
  // Bulk-only (multi-select toolbar)
  | "bulk-move"
  | "bulk-delete";

export type ObjectActionGroupKey =
  | "navigation"
  | "organization"
  | "sharing"
  | "favorites"
  | "file"
  | "canvas-order"
  | "collaboration"
  | "destructive"
  | "bulk";

export type CanvasNodeType =
  | "entity"
  | "text-note"
  | "canvas-text"
  | "canvas-shape"
  | "section"
  | "widget-node"
  | "chat-session"
  | "canvas-connector";

export type ObjectKind =
  | { type: "workspace"; scope: "team" | "private" }
  | { type: "canvas" }
  | { type: "chat" }
  | {
      type: "file-record";
      class: "workspace" | "canvas" | "flow-session";
      isFolder: boolean;
    }
  | { type: "connector" }
  /** Canvas/chat folder (the shared `folders` table), not a file-record folder. */
  | { type: "resource-folder"; resourceType: "canvas" | "flow-session" }
  | { type: "skill" }
  | { type: "skill-template" }
  | { type: "canvas-node"; nodeType: CanvasNodeType; entityBase?: string }
  | { type: "canvas-edge" }
  | {
      type: "multi-selection";
      kinds: Array<"canvas" | "chat" | "file" | "folder">;
    };

export type ObjectKindKey = string;

export type ActionSurface =
  | "left-rail"
  | "main-view"
  | "context-menu"
  | "floating-toolbar"
  | "pane-header"
  | "selection-bar"
  | "page-header"
  | "card-overlay";

export const ACTION_SURFACES: readonly ActionSurface[] = [
  "left-rail",
  "main-view",
  "context-menu",
  "floating-toolbar",
  "pane-header",
  "selection-bar",
  "page-header",
  "card-overlay",
] as const;

export type ActionPresentation =
  | "menu-item"
  | "menu-item-with-shortcut"
  | "icon-button"
  | "primary-button"
  | "secondary-button"
  | "corner-star"
  | "inline-edit"
  | "toolbar-primary"
  | "toolbar-more-submenu"
  | "toolbar-utility"
  | "hidden";

export type ActionScope =
  | "canonical"
  | "favorites"
  | "shared-with-me"
  | "canvas-explorer"
  | "trash";

export type TrashPolicy = "confirm" | "undo-toast" | "none";

export interface ActionLabelContext {
  kind: ObjectKind;
  count?: number;
  name?: string;
  isPublished?: boolean;
  isFavorite?: boolean;
}

export interface ObjectActionDefinition {
  id: ObjectActionId;
  label: string | ((ctx: ActionLabelContext) => string);
  /** Static, or kind-sensitive like `label` (e.g. `open` shows a folder glyph
   *  on file folders and the open-in-pane glyph on files). */
  icon: LucideIcon | ((ctx: ActionLabelContext) => LucideIcon);
  destructive?: boolean;
  shortcut?: string;
  group: ObjectActionGroupKey;
  trashPolicy?: TrashPolicy;
}

export type ObjectActionPredicate =
  | { not: ObjectActionPredicate }
  | { all: ObjectActionPredicate[] }
  | { any: ObjectActionPredicate[] }
  | { permission: string }
  | { scope: ActionScope }
  | { state: keyof ObjectActionContext["state"] }
  | { kind: Partial<ObjectKindFilter> };

export interface ObjectKindFilter {
  type: ObjectKind["type"];
  scope?: "team" | "private";
  isFolder?: boolean;
  nodeType?: CanvasNodeType;
  entityBase?: string;
}

export interface ObjectActionEntry {
  id: ObjectActionId;
  surfaces: readonly ActionSurface[];
  presentation?:
    | ActionPresentation
    | Partial<Record<ActionSurface, ActionPresentation>>;
  hiddenWhen?: ObjectActionPredicate;
  disabledWhen?: ObjectActionPredicate;
  /**
   * Expand this action into runtime variants (e.g. file-record export formats).
   * See {@link attachFileRecordDownloadVariants}.
   */
  variantSource?: "file-record-export";
}

export interface ObjectActionVariant {
  id: string;
  label: string;
  disabled?: boolean;
  handler: () => void;
}

export interface ObjectTypeConfig {
  /** Merge actions from another manifest kind (child overrides by id). */
  extends?: ObjectKindKey;
  actions: readonly ObjectActionEntry[];
}

export type ObjectActionManifest = Record<ObjectKindKey, ObjectTypeConfig>;

export interface ObjectActionContext {
  surface: ActionSurface;
  kind: ObjectKind;
  permissions: Record<string, boolean>;
  scope: ActionScope;
  state: {
    isArchived?: boolean;
    isPublished?: boolean;
    isFavorite?: boolean;
    canEdit?: boolean;
    canShare?: boolean;
    canDelete?: boolean;
    selectionCount?: number;
    selectionSingle?: boolean;
    canRenameSelection?: boolean;
    canDuplicateSelection?: boolean;
    hasChatsInSelection?: boolean;
    hasWebLink?: boolean;
    hasDetailView?: boolean;
    copyDisabled?: boolean;
    zOrderDisabled?: boolean;
    isPrivateWorkspace?: boolean;
    /** File-record on canvas toolbar / context menu (not workspace list menus). */
    onCanvasDetail?: boolean;
    /** The file's canvas node has `hidden: true` — drives Hide vs Show on canvas. */
    hiddenOnCanvas?: boolean;
    showPublish?: boolean;
    showVersionHistory?: boolean;
    /** Container (workspace/folder) surfaced where files can be uploaded (files tab). */
    canUploadFiles?: boolean;
    /** Skill is in "auto" mode (enabled) — drives set-auto/set-manual visibility. */
    isSkillEnabled?: boolean;
    /** Skill template can be unpublished by the current user (publisher/org admin). */
    canUnpublish?: boolean;
  };
}

export interface ResolvedObjectAction {
  id: ObjectActionId;
  definition: ObjectActionDefinition;
  presentation: ActionPresentation;
  disabled: boolean;
  hidden: boolean;
  handler: () => void;
  /** Populated at runtime for compound actions (e.g. download export formats). */
  variants?: ObjectActionVariant[];
}

export type ObjectActionHandlers = Partial<Record<ObjectActionId, () => void>>;

export interface StaticActionShell {
  id: ObjectActionId;
  definition: ObjectActionDefinition;
  presentation: ActionPresentation;
  hiddenWhen?: ObjectActionPredicate;
  disabledWhen?: ObjectActionPredicate;
}

export function objectKindKey(kind: ObjectKind): ObjectKindKey {
  switch (kind.type) {
    case "workspace":
      return kind.scope === "private" ? "workspace:private" : "workspace";
    case "canvas":
      return "canvas";
    case "chat":
      return "chat";
    case "file-record":
      return kind.isFolder ? "file-record:folder" : "file-record";
    case "resource-folder":
      return kind.resourceType === "canvas"
        ? "resource-folder:canvas"
        : "resource-folder:chat";
    case "connector":
      return "connector";
    case "skill":
      return "skill";
    case "skill-template":
      return "skill-template";
    case "canvas-node":
      return `canvas-node:${kind.nodeType}`;
    case "canvas-edge":
      return "canvas-edge";
    case "multi-selection":
      return "multi-selection";
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function resolvePresentation(
  entry: ObjectActionEntry,
  surface: ActionSurface,
): ActionPresentation {
  if (!entry.presentation) {
    return "menu-item";
  }
  if (typeof entry.presentation === "string") {
    return entry.presentation;
  }
  return entry.presentation[surface] ?? "menu-item";
}
